import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Slider from '@mui/material/Slider';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useLocation, useNavigate } from 'react-router-dom';
import { constants } from '../../constants';
import useMyMApi from '../../hooks/useMyMApi';
import useValidationRules from '../../hooks/useValidationRules';
import useWebContext from '../../hooks/useWebContext';
import { MyMButton, MyMCloseButton } from '../../ui-elements/MyMButton';
import { MyMImage } from '../../ui-elements/MyMImage';
import { MyMLabel } from '../../ui-elements/MyMLabel';
import { MyMProgress } from '../../ui-elements/MyMProgress';
import { ValidatableCheckBoxList } from '../../ui-parts/ValidatableCheckBoxList';
import ValidatableRadio from '../../ui-parts/ValidatableRadio';
import ValidatableTextBox from '../../ui-parts/ValidatableTextBox';
import { mediaQuery, useMediaQuery } from '../../hooks/useMediaQuery';

const AnswerSurvey = () => {
  const screenId = 'MyM801';
  const apiRequest = useMyMApi(screenId);
  const { controlSettings, messageList, screenSettings } = useWebContext(screenId);
  const validationRules = useValidationRules();
  const navigate = useNavigate();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const surveyIdFromUrl = searchParams.get('surveyId');
  const [sliderValues, setSliderValues] = useState({});
  const [textValues, setTextValues] = useState({});
  const isPc = useMediaQuery(mediaQuery.pc);
  // 2025.7.7 MyMine Version3.0対応 Add Start
  const [questionAnswerOptionValues, setQuestionAnswerOptionValues] = useState([]);
  // 2025.7.7 MyMine Version3.0対応 Add End
  const [isAnswered, setIsAnswered] = useState(false);
  const [answerSurveyItem, setAnswerSurveyItem] = useState({
    SurveyMstData: {
      Description: '',
    },
    QuestionList: [],
  });
  const [isLoading, setIsLoading] = useState(false);

  const { formState, control, trigger, handleSubmit } = useForm({
    mode: 'onSubmit',
  });

  const marks = Array.from({ length: 11 }, (_, i) => ({
    value: i,
    label: i.toString(),
  }));


  useEffect(() => {
    if (surveyIdFromUrl) {
      apiRequest.requestJson(
        `api/survey/answer-load?surveyId=${surveyIdFromUrl}`,
        'GET',
        (result) => {
          setAnswerSurveyItem(result);

          // 2025.7.7 MyMine Version3.0対応 Add Start
          for (const question of result.QuestionList.filter(x => x.AnswerType === constants.SurveyAnswerType.Radiobutton || x.AnswerType === constants.SurveyAnswerType.Checkbox)) {
            questionAnswerOptionValues.push({ QuestionId: question.QuestionId, OptionList: [], OtherText: '' });
          }
          // 2025.7.7 MyMine Version3.0対応 Add End

          if (result.AnswerList && result.AnswerList.length > 0) {
            const newSliderValues = {};
            const newTextValues = {};
            setIsAnswered(true);

            result.AnswerList.forEach(answer => {
              if (answer.AnswerOption !== null) {
                newSliderValues[answer.QuestionId] = parseInt(answer.AnswerOption);
              }
              if (answer.AnswerText !== null) {
                newTextValues[answer.QuestionId] = answer.AnswerText;
              }

              // 2025.7.7 MyMine Version3.0対応 Add Start
              if (answer.AnswerRadio) {
                const questionAnswerOption = questionAnswerOptionValues.find(x => x.QuestionId === answer.QuestionId);
                questionAnswerOption.Option = Number(answer.AnswerRadio);
              }

              if (answer.AnswerCheckbox) {
                const questionAnswerOption = questionAnswerOptionValues.find(x => x.QuestionId === answer.QuestionId);
                questionAnswerOption.OptionList = answer.AnswerCheckbox.split(',').map(checkValue => Number(checkValue));
              }

              if (answer.AnswerOther) {
                const questionAnswerOption = questionAnswerOptionValues.find(x => x.QuestionId === answer.QuestionId);
                questionAnswerOption.OtherText = answer.AnswerOther;
              }
              // 2025.7.7 MyMine Version3.0対応 Add End
            });
            setSliderValues(newSliderValues);
            setTextValues(newTextValues);
          }

          // 2025.7.7 MyMine Version3.0対応 Add Start
          setQuestionAnswerOptionValues(questionAnswerOptionValues);
          // 2025.7.7 MyMine Version3.0対応 Add End

          if (result.QuestionList.length === 0) {
            navigate('/MyM999', { state: { message: constants.Message.InvalidRequest } });
          }
        },
        () => {
          navigate('/MyM999', { state: { message: constants.Message.InvalidRequest } });
        }
      );
    } else {
      navigate('/MyM999', { state: { message: constants.Message.InvalidRequest } });
    }
  }, []);

  const handleSliderChange = (questionId, newValue) => {
    setSliderValues((prevValues) => ({
      ...prevValues,
      [questionId]: newValue ?? 0,
    }));
  };

  const handleTextChange = (questionId, event) => {
    setTextValues((prevValues) => ({
      ...prevValues,
      [questionId]: event.target.value ?? "",
    }));
  };

  const callbackSuccessSubmit = () => {
    setIsLoading(false);
    window.alert(messageList.MyM_MSG_I_0103);

    if (window.opener) {
      window.opener.location.reload();
    }

    window.close();
  };

  const callbackFailedContentText = (error) => {
    setIsLoading(false);
    navigate('/MyM999', { state: { message: constants.Message.SystemError } });
    return;
  };

  /**
   * Submit処理
   */
  const doSubmit = () => {
    if (!window.confirm(messageList.MyM_MSG_I_0102)) return;

    setIsLoading(true);

    const answerList = answerSurveyItem.QuestionList.map((question) => {

      const answerData = {
        QuestionId: question.QuestionId,
      };

      switch (question.AnswerType) {
        case constants.SurveyAnswerType.Option:
          answerData.AnswerOption = sliderValues[question.QuestionId] ?? 5;
          break;
        case constants.SurveyAnswerType.Text:
          answerData.AnswerText = textValues[question.QuestionId] ?? '';
          answerData.TextCount = (textValues[question.QuestionId]?.length ?? 0);
          break;
        // 2025.7.7 MyMine Version3.0対応 Add Start
        case constants.SurveyAnswerType.Checkbox:
          const multiSelectAnswer = questionAnswerOptionValues.find(x => x.QuestionId === question.QuestionId);
          answerData.AnswerCheckbox = multiSelectAnswer?.OptionList.join(',');
          if (isCheckedOtherOption(question.QuestionId)) {
            answerData.AnswerOther = multiSelectAnswer.OtherText;
          }
          break;
        case constants.SurveyAnswerType.Radiobutton:
          const singleSelectAnswer = questionAnswerOptionValues.find(x => x.QuestionId === question.QuestionId);
          answerData.AnswerRadio = singleSelectAnswer?.Option;
          if (isCheckedOtherOption(question.QuestionId)) {
            answerData.AnswerOther = singleSelectAnswer.OtherText;
          }
          break;
        default:
          break;
        // 2025.7.7 MyMine Version3.0対応 Add End
      }

      return answerData;
    });

    const surveyAnswer = {
      AnswerList: answerList,
    };

    apiRequest.requestJson('api/survey/insert-answer', 'POST', callbackSuccessSubmit, callbackFailedContentText, surveyAnswer);
  };

  /**
   * Submitエラー処理
   */
  const onError = () => {
    window.alert(messageList.MyM_MSG_E_0003);
  };

  // 2025.7.7 MyMine Version3.0対応 Add Start
  /**
   * Other Optionにチェックが入っているか否か
   * @param {Number} questionId QuestionId
   * @returns チェックが入っている場合、True
   */
  const isCheckedOtherOption = (questionId) => {

    const question = answerSurveyItem.QuestionList.find(x => x.QuestionId === questionId);
    const otherOptionId = question.QuestionOptionList.find(y => y.OtherFlg)?.OptionId;

    switch (question.AnswerType) {
      case constants.SurveyAnswerType.Checkbox:
        return questionAnswerOptionValues.find(x => x.QuestionId === questionId).OptionList.includes(otherOptionId);

      case constants.SurveyAnswerType.Radiobutton:
        return questionAnswerOptionValues.find(x => x.QuestionId === questionId).Option === otherOptionId;

      default:
        return false;
    }
  }
  // 2025.7.7 MyMine Version3.0対応 Add End

  if (answerSurveyItem.QuestionList.length === 0)
    return <MyMProgress />;

  return (
    <Box sx={{ padding: { xs: '0px 0px', md: '5px 50px' }, height: '100%', overflowY: 'auto', position: 'relative' }}>
      {isLoading && <MyMProgress />}
      <Grid container alignItems="flex-start" sx={isPc ? { px : 6 } : { px: '15px' }}>
        <>
          <Grid item xs={12} alignItems="center">
            <Stack
              direction={{ xs: 'column', md: 'row' }}
              justifyContent={{ xs: 'center', md: 'start' }}
              alignItems={{ xs: 'center', md: 'start' }}
              sx={{ '> img': { marginLeft: { xs: '-10px', md: -10 }},}}
            >
              <MyMImage
                screenId={screenId}
                filePath={controlSettings.ImageList.imgMyMineLogo}
                alt="Logo"
                style={{ width: '350px', height: '110px'}}
                display="block"
              />
              <MyMLabel
                sx={{
                  fontSize: { xs: '20px !important', md: '30px !important' },
                  paddingTop: { md: '50px' },
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                }}>
                {controlSettings.LabelList.lblMyMineMessage}
              </MyMLabel>

            </Stack>
            <Grid sx={{ width: '100%' }} >
              <Grid >
                <MyMLabel
                  sx={{
                    fontSize: { xs: '15px !important', md: '16px !important' },
                    alignItems: 'center',
                    backgroundColor: '#D3D3D3',
                    marginTop: 1,
                    padding: isPc?'10px 30px':'10px',
                    whiteSpace: 'pre-wrap',
                  }}
                >
                  {answerSurveyItem.SurveyMstData.Description}
                </MyMLabel>
              </Grid>
            </Grid>
          </Grid>

          {answerSurveyItem.QuestionList.map((question, index) => (
            <Grid
              item
              xs={12}
              key={question.QuestionId}
              sx={{
                // 2025.7.7 MyMine Version3.0対応 Add Start
                '.required:after': { content: `" ${controlSettings.LabelList.lblQuestionRequired}"`, color: 'red', fontSize: { xs: 14, md: 17 } },
                'p.MuiTypography-root': { fontSize: { xs: '17px !important', md: '20px !important' } },
                // 2025.7.7 MyMine Version3.0対応 Add End
              }}>
              <Grid container className='mt-5'>
                <Grid item xs={isPc ?12:11.3} md={9} sx={{ display: '-webkit-inline-box' }}>
                  {/* 2025.7.7 MyMine Version3.0対応 Modify Start */}
                  <MyMLabel>{index + 1}. </MyMLabel>
                  <MyMLabel className={`${question.RequiredFlg ? 'required' : ''} mb-2`}>{question.QuestionText}</MyMLabel>
                  {/* 2025.7.7 MyMine Version3.0対応 Modify End */}
                </Grid>
                <Grid item xs={12} md={question.AnswerType === constants.SurveyAnswerType.Option ? 4 : 12}>
                  {question.AnswerType === constants.SurveyAnswerType.Option ?
                    // Scale
                    (
                      <Box sx={{ display: 'flex', justifyContent: 'end', alignItems: 'center', gap: '20px', margin: isPc ? "20px 20px 0 20px" :"40px 0px 0px"  }}>
                        <MyMImage
                          screenId={screenId}
                          filePath={controlSettings.ImageList.imgAngree}
                          alt="Angry"
                          style={{ width: '30px', height: '30px' }}
                        />
                        <Box sx={{ width: '100%' }}>
                          <Slider
                            aria-label="Custom marks"
                            value={sliderValues[question.QuestionId] ?? 5}
                            onChange={(e, newValue) => handleSliderChange(question.QuestionId, newValue)}
                            valueLabelDisplay="auto"
                            step={1}
                            marks={marks}
                            min={1}
                            max={10}
                            disabled={isAnswered}
                          />
                        </Box>
                        <MyMImage
                          screenId={screenId}
                          filePath={controlSettings.ImageList.imgSmile}
                          alt="Smile"
                          style={{ width: '30px', height: '30px' }}
                        />
                      </Box>
                    )
                    : question.AnswerType === constants.SurveyAnswerType.Text ?
                      // Text
                      (
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '10px' }}>
                          <ValidatableTextBox
                            value={textValues[question.QuestionId] ?? ''}
                            control={control}
                            width="100%"
                            rows={5}
                            multiline={true}
                            formState={formState}
                            name={`answerText_${question.QuestionId}`}
                            onChange={(e) => handleTextChange(question.QuestionId, e)}
                            disabled={isAnswered}
                            maxLength={screenSettings.MaxTextLength}
                            rules={{
                              ...validationRules.conditionalRequired(question.RequiredFlg),
                              ...validationRules.maxLength(screenSettings.MaxTextLength),
                            }}
                          />
                          <Typography variant="caption" align="right" sx={{ width: '100%' }}>
                            {textValues[question.QuestionId]?.length || 0} / {screenSettings.MaxTextLength}
                          </Typography>
                        </Box>
                      )
                      // 2025.7.7 MyMine Version3.0対応 Add Start
                      : question.AnswerType === constants.SurveyAnswerType.Checkbox ?
                        // Multi Select
                        (
                          <Box sx={{
                            marginLeft: 3,
                            '.MuiCheckbox-root': { paddingLeft: 0 },
                            '.MuiFormGroup-root > label': {
                              flexDirection: 'row',
                              margin: 0,
                              width: { xs: '85%', md: 'fit-content' },
                              wordWrap: 'break-word',
                              'span.MuiTypography-root': { width: '100%' },
                              '@media (min-width:900px) and (max-width:1080px)': {
                                width: '85%',
                              },
                            },
                          }}>
                            <ValidatableCheckBoxList
                              controllerName={`answer_${question.QuestionId}_check`}
                              keyValueList={question.QuestionOptionList.map(x => ({ Key: x.OptionId, Value: x.OptionText }))}
                              checkedList={questionAnswerOptionValues.find(x => x.QuestionId === question.QuestionId).OptionList}
                              onChange={((e, item) => {
                                const questionAnswerOption = questionAnswerOptionValues.find(x => x.QuestionId === question.QuestionId);
                                questionAnswerOption.OptionList = e.target.checked ? [...questionAnswerOption.OptionList, item.Key] : questionAnswerOption.OptionList.filter(x => x !== item.Key)
                                setQuestionAnswerOptionValues([...questionAnswerOptionValues]);
                              })}
                              control={control}
                              rules={{
                                ...validationRules.listRequired(
                                  questionAnswerOptionValues.find(x => x.QuestionId === question.QuestionId).OptionList,
                                  () => question.RequiredFlg === true
                                ),
                              }}
                              formState={formState}
                              trigger={trigger}
                              checkboxProps={{ disabled: isAnswered }}
                              isVertical={true}
                              renderOptionSuffixItem={(item) => question.QuestionOptionList.find(x => x.OptionId === item.Key).OtherFlg &&
                                <ValidatableTextBox
                                  sx={{ paddingLeft: 4, width: { xs: '100%', md: 500 } }}
                                value={!isCheckedOtherOption(question.QuestionId) ? '' : questionAnswerOptionValues.find(x => x.QuestionId === question.QuestionId).OtherText}
                                  control={control}
                                  formState={formState}
                                  name={`option_other_${question.QuestionId}`}
                                  onChange={(e) => {
                                    questionAnswerOptionValues.find(x => x.QuestionId === question.QuestionId).OtherText = e.target.value
                                    setQuestionAnswerOptionValues([...questionAnswerOptionValues]);
                                  }}
                                  // Otherにチェックが入っている 又は 回答済みの場合、非活性
                                  disabled={!isCheckedOtherOption(question.QuestionId) || isAnswered}
                                  maxLength={100}
                                  rules={{
                                    ...validationRules.maxLength(100),
                                  }}
                                />
                              }
                            />
                          </Box>
                        )
                        : question.AnswerType === constants.SurveyAnswerType.Radiobutton ?
                          // Single Select
                          (
                            <Box sx={{
                              marginLeft: 3,
                              '.MuiFormGroup-root > label':
                              {
                                wordBreak: 'break-word',
                                ' span.MuiTypography-root': { width: isPc?'100%':'85%' }
                              },
                              '> .MuiFormControl-root': { width: '100%' },
                              'label.MuiFormControlLabel-root':
                              {
                                width: { xs: '100%', md: 'fit-content' },
                              }, }}>
                              <ValidatableRadio
                                name={`answer_${question.QuestionId}_radio`}
                                control={control}
                                radioList={question.QuestionOptionList.map(x => ({ Key: Number(x.OptionId), Value: x.OptionText }))}
                                checkValue={questionAnswerOptionValues.find(x => x.QuestionId === question.QuestionId).Option}
                                onChange={((e) => {
                                  const questionAnswerOption = questionAnswerOptionValues.find(x => x.QuestionId === question.QuestionId);
                                  questionAnswerOption.Option = Number(e.target.value);
                                  setQuestionAnswerOptionValues([...questionAnswerOptionValues]);
                                })}
                                rules={{
                                  ...validationRules.conditionalRequired(question.RequiredFlg)
                                }}
                                formState={formState}
                                radioProps={{ disabled: isAnswered }}
                                // Otherフラグが立っている場合、Otherテキストボックスを描画
                                renderOptionSuffixItem={(item) => question.QuestionOptionList.find(x => x.OptionId === item.Key).OtherFlg &&
                                  <ValidatableTextBox
                                    sx={{ paddingLeft: 4, width: { xs: '100%', md: 500 }, display: 'flex' }}
                                  value={!isCheckedOtherOption(question.QuestionId) ? '' : questionAnswerOptionValues.find(x => x.QuestionId === question.QuestionId).OtherText}
                                    control={control}
                                    formState={formState}
                                    name={`option_other_${question.QuestionId}`}
                                    onChange={(e) => {
                                      questionAnswerOptionValues.find(x => x.QuestionId === question.QuestionId).OtherText = e.target.value
                                      setQuestionAnswerOptionValues([...questionAnswerOptionValues]);
                                    }}
                                    // Otherにチェックが入っている 又は 回答済みの場合、非活性
                                    disabled={!isCheckedOtherOption(question.QuestionId) || isAnswered}
                                    maxLength={100}
                                    rules={{
                                      ...validationRules.maxLength(100),
                                    }}
                                  />}
                              />
                            </Box>
                          )
                          : <></>
                    // 2025.7.7 MyMine Version3.0対応 Add End
                  }
                </Grid>
              </Grid>
            </Grid>
          ))}

          <Grid container spacing={3} justifyContent="flex-end" className='py-4'>
            {!isAnswered && (
              <Grid item>
                <MyMButton sx={{ backgroundColor: 'blue', color: 'white' }} onClick={handleSubmit(doSubmit, onError)}>{controlSettings.LabelList.btnSubmit}</MyMButton>
              </Grid>
            )}
            <Grid item>
              <MyMCloseButton sx={{ backgroundColor: 'blue', color: 'white' }} closeText={controlSettings.LabelList.btnBack} />
            </Grid>
          </Grid>
        </>
      </Grid>
    </Box>
  );
};

export default AnswerSurvey;